import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, SPACE } from '@/nova/theme/tokens';

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Matches the shared field label (Promotions / Vibe / Prompt) so all four headings read as one set. */
/** Icon + label row for the field caption. */
export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLORS.textMuted};
`;

export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const Hint = styled(Text)`
  font-size: 11.5px;
  line-height: 1.4;
  color: ${COLORS.textFaint};
`;

/* A bordered drop zone that lights up on drag-over / focus, matching the banner prompt composer. */
export const Board = styled.div<{ $dragging: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  background: ${({ $dragging }) => ($dragging ? COLORS.surfaceMuted : COLORS.surfaceFaint)};
  border: 1px solid ${({ $dragging }) => ($dragging ? COLORS.accent : COLORS.hairline)};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-within {
    border-color: ${COLORS.accent};
  }
`;

/** The board's "n/max" count (showCount), in its lower-right corner like the direction field's character count. */
export const Count = styled.span`
  position: absolute;
  right: ${SPACE[12]};
  bottom: ${SPACE[8]};
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
  pointer-events: none;
`;

export const Thumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const uploadPulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

export const Thumb = styled.div<{ $pending?: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 9px;
  overflow: hidden;
  border: 1px solid ${({ $pending }) => ($pending ? COLORS.accent : COLORS.hairline)};
  background: ${COLORS.surfaceInset};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    ${({ $pending }) =>
      $pending
        ? css`
            animation: ${uploadPulse} ${ANIM.LOGO_PLACEHOLDER_PULSE_MS}ms ease-in-out infinite;
          `
        : ''}
  }
`;

/* A thumb that opens its image full size: the whole tile is the button, under the remove button (which sits on
   top of it, positioned, so a click on the x never opens the image). */
export const Open = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: -2px;
  }
`;

export const Remove = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  cursor: pointer;
  color: ${COLORS.textBright};
  background: ${COLORS.surfaceCard};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }
`;

/* The add affordance: a dashed tile the same size as a thumbnail (a <label> wrapping a hidden file input).
   While `$disabled` (a compose/generate is in flight) it stays visible but greys out and stops responding,
   mirroring the sibling fields, rather than vanishing. */
export const AddTile = styled.label<{ $disabled?: boolean; $wide?: boolean; $primary?: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 54px;
  height: 54px;
  border-radius: 9px;
  border: 1px dashed ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  font-size: 10px;
  font-weight: 600;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? COLORS.hairline : COLORS.accent)};
    color: ${({ $disabled }) => ($disabled ? COLORS.textMuted : COLORS.textBright)};
  }

  input {
    display: none;
  }

  /* Wide (the tile has its own words, e.g. "Add images"): the thumbnail's height but as wide as its words, the
     mark and the words side by side on one line, where a thumbnail-sized square would wrap them. */
  ${({ $wide }) =>
    $wide &&
    css`
      flex-direction: row;
      gap: ${SPACE[8]};
      width: auto;
      padding: 0 ${SPACE[16]};
      white-space: nowrap;
      font-size: 12.5px;
    `}

  /* Primary (primaryAdd): where to click next, in the CTA orange (its outline, its "+" and its words); hovered, it
     takes the orange's faint wash. */
  ${({ $primary, $disabled }) =>
    $primary &&
    css`
      border-color: ${COLORS.ctaBorder};
      color: ${COLORS.ctaSolid};
      transition:
        border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
        background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
        opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

      &:hover {
        border-color: ${$disabled ? COLORS.ctaBorder : COLORS.ctaSolid};
        color: ${COLORS.ctaSolid};
        background: ${$disabled ? 'transparent' : COLORS.ctaTint};
      }
    `}
`;
